import { CommonModule } from '@angular/common';
import { Component, ViewChild, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  DeleteImpact,
  LidarUploadRow,
  ScanJob,
  SystemLocation,
  SbcApiService,
} from '../services/sbc-api.service';
import { SessionService } from '../../../core/services/session.service';
import {
  SCAN_ELIGIBLE_TYPES,
  LocTreeNode,
  buildLocationTree,
} from './lidar-location.util';
import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';
import { LocationLidarPanelComponent } from './location-lidar-panel.component';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-lidar-survey',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    LocationLidarPanelComponent,
    ConfirmDialogComponent,
    PageRefreshButtonComponent,
    PageBackButtonComponent,
  ],
  templateUrl: './lidar-survey.component.html',
  styleUrl: './lidar-survey.component.css',
})
export class LidarSurveyComponent {
  private readonly api = inject(SbcApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly session = inject(SessionService);

  @ViewChild(LocationLidarPanelComponent) locationPanel?: LocationLidarPanelComponent;

  readonly loading = signal(false);
  readonly jobsLoading = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly toast = signal('');

  readonly locationId = signal<number | null>(null);
  readonly locations = signal<SystemLocation[]>([]);

  readonly uploadScanOpen = signal(false);
  readonly uploadPickerExpandedIds = signal<Set<number>>(new Set());
  readonly allScanJobs = signal<ScanJob[]>([]);
  readonly uploadFormErrors = signal<{ file?: string; location?: string; form?: string }>({});
  readonly deleteConfirmOpen = signal(false);
  readonly deleteConfirmMessage = signal('');
  readonly deleteImpactLoadingId = signal<number | null>(null);
  readonly deletingScanId = signal<number | null>(null);
  private readonly pendingDeleteScan = signal<ScanJob | null>(null);

  scanUploadForm = {
    locationId: null as number | null,
    scanType: 'drone' as 'drone' | 'vehicle',
    selectedFile: null as File | null,
  };

  readonly canManageLocations = computed(() =>
    ['officer', 'config_admin'].includes(this.session.role())
  );

  readonly uploadLocationTree = computed(() => buildLocationTree(this.locations()));

  readonly hasSelectedLocation = computed(() => {
    const id = this.locationId();
    if (id == null) return false;
    const loc = this.locations().find((l) => l.id === id);
    return !!loc && loc.statusCd === 'active' && SCAN_ELIGIBLE_TYPES.has(loc.locationTypeCode);
  });

  readonly selectedLocation = computed(() => {
    const id = this.locationId();
    if (id == null) return null;
    return this.locations().find((l) => l.id === id) ?? null;
  });

  readonly focusLocation = computed(() => this.locationId() != null);

  readonly allUploads = computed((): LidarUploadRow[] => {
    const locById = new Map(this.locations().map((l) => [l.id, l]));
    return [...this.allScanJobs()]
      .sort((a, b) => {
        const ta = a.createdAt ? Date.parse(a.createdAt) : a.id;
        const tb = b.createdAt ? Date.parse(b.createdAt) : b.id;
        return tb - ta;
      })
      .map((job) => {
        const loc = job.locationId ? locById.get(job.locationId) : null;
        return {
          job,
          locationId: job.locationId ?? null,
          locationName: loc?.name ?? (job.pilotAreaId ? `Pilot area #${job.pilotAreaId}` : 'Unlinked'),
          locationCode: loc?.code ?? '—',
          locationTypeCode: loc?.locationTypeCode ?? '—',
        };
      });
  });

  constructor() {
    this.route.queryParamMap.subscribe((params) => {
      const raw = params.get('locationId') ?? params.get('sectorId');
      const id = raw ? Number(raw) : null;
      this.locationId.set(id != null && !Number.isNaN(id) ? id : null);
    });

    effect(() => {
      this.session.authorityId();
      untracked(() => this.reloadLocations());
    });
  }

  reloadLocations(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listSystemLocations({ includeInactive: 'true' }).subscribe({
      next: (res) => {
        this.locations.set(res.data || []);
        this.loading.set(false);
        this.loadAllScanJobs();
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.error?.message || 'Failed to load locations');
      },
    });
  }

  loadAllScanJobs(): void {
    this.jobsLoading.set(true);
    this.api.listScanJobs().subscribe({
      next: (res) => {
        this.allScanJobs.set(res.data || []);
        this.jobsLoading.set(false);
      },
      error: () => {
        this.allScanJobs.set([]);
        this.jobsLoading.set(false);
      },
    });
  }

  reload(): void {
    this.reloadLocations();
  }

  openUploadRow(row: LidarUploadRow): void {
    if (row.locationId != null) {
      this.selectLocation(row.locationId);
    }
  }

  canDeleteScan(job: ScanJob): boolean {
    return this.canManageLocations() &&
      String(job.statusCd || '').toLowerCase() !== 'processing';
  }

  requestDeleteScan(job: ScanJob): void {
    if (!this.canDeleteScan(job) || this.deleteImpactLoadingId() != null) return;
    this.deleteImpactLoadingId.set(job.id);
    this.api.getScanDeleteImpact(job.id).subscribe({
      next: (res) => {
        this.deleteImpactLoadingId.set(null);
        this.pendingDeleteScan.set(job);
        this.deleteConfirmMessage.set(this.scanDeleteMessage(job, res.data));
        this.deleteConfirmOpen.set(true);
      },
      error: (err) => {
        this.deleteImpactLoadingId.set(null);
        this.showToast(err?.error?.message || 'Unable to check scan dependencies');
      },
    });
  }

  confirmDeleteScan(): void {
    const job = this.pendingDeleteScan();
    if (!job || this.deletingScanId() != null) return;
    this.deleteConfirmOpen.set(false);
    this.deletingScanId.set(job.id);
    this.api.deleteScanJob(job.id).subscribe({
      next: (res) => {
        this.deletingScanId.set(null);
        this.pendingDeleteScan.set(null);
        this.showToast(res.meta?.['message'] as string || 'LiDAR file removed');
        this.loadAllScanJobs();
        this.locationPanel?.reload();
      },
      error: (err) => {
        this.deletingScanId.set(null);
        this.showToast(err?.error?.message || 'Failed to remove LiDAR file');
      },
    });
  }

  cancelDeleteScan(): void {
    this.deleteConfirmOpen.set(false);
    this.pendingDeleteScan.set(null);
  }

  private scanDeleteMessage(job: ScanJob, impact: DeleteImpact): string {
    const fileName = this.fileNameFromUri(job.rawStorageUri);
    if (!impact.hasDerivedData) {
      return `Delete ${fileName}? This action cannot be undone.`;
    }
    return `Delete ${fileName}? Warning: this scan has already been processed (${impact.processingRunCount || 0} run(s), ${impact.measurementCount} measurement(s), ${impact.flagCount} flag(s)). Related data will be retained for audit, but reprocessing from this file will no longer be possible.`;
  }

  clearLocationSelection(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { locationId: null, sectorId: null },
      queryParamsHandling: 'merge',
    });
  }

  selectLocation(id: number): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { locationId: id, sectorId: null },
      queryParamsHandling: 'merge',
    });
  }

  openUploadScan(scanType: 'drone' | 'vehicle' = 'drone'): void {
    this.scanUploadForm = {
      locationId: this.locationId(),
      scanType,
      selectedFile: null,
    };
    this.clearUploadFormErrors();
    this.initUploadTreeExpansion();
    this.uploadScanOpen.set(true);
  }

  closeUploadScan(): void {
    this.uploadScanOpen.set(false);
    this.clearUploadFormErrors();
  }

  isUploadTreeExpanded(id: number): boolean {
    return this.uploadPickerExpandedIds().has(id);
  }

  toggleUploadTreeExpand(id: number, event?: Event): void {
    event?.stopPropagation();
    const next = new Set(this.uploadPickerExpandedIds());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.uploadPickerExpandedIds.set(next);
  }

  isUploadLocationSelectable(loc: SystemLocation): boolean {
    return loc.statusCd === 'active' && SCAN_ELIGIBLE_TYPES.has(loc.locationTypeCode);
  }

  isUploadLocationSelected(id: number): boolean {
    return this.scanUploadForm.locationId === id;
  }

  onUploadTreeLabelClick(node: LocTreeNode): void {
    if (this.isUploadLocationSelectable(node.location)) {
      this.scanUploadForm.locationId = node.location.id;
      this.uploadFormErrors.update((e) => ({ ...e, location: undefined, form: undefined }));
      return;
    }
    if (node.children.length) {
      this.toggleUploadTreeExpand(node.location.id);
    }
  }

  selectedUploadLocationLabel(id: number | null): string {
    if (id == null) return '';
    const loc = this.locations().find((l) => l.id === id);
    return loc ? `${loc.name} · ${loc.code}` : '';
  }

  typeInitial(name: string): string {
    return (name || '?').charAt(0).toUpperCase();
  }

  typeTone(name: string): string {
    const key = (name || '').toLowerCase();
    if (key.includes('authority')) return 'auth';
    if (key.includes('zone')) return 'zone';
    if (key.includes('sector')) return 'sector';
    if (key.includes('plot')) return 'plot';
    return 'default';
  }

  private initUploadTreeExpansion(): void {
    const roots = this.uploadLocationTree();
    const ids = new Set<number>();
    for (const node of roots) {
      if (node.children.length) ids.add(node.location.id);
    }
    this.uploadPickerExpandedIds.set(ids);
  }

  onScanFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    this.scanUploadForm.selectedFile = file;
    if (file) {
      this.uploadFormErrors.update((e) => ({ ...e, file: undefined, form: undefined }));
    }
  }

  submitUploadScan(): void {
    const loc =
      this.selectedLocation() ??
      this.locations().find((l) => l.id === this.scanUploadForm.locationId) ??
      null;
    const file = this.scanUploadForm.selectedFile;

    if (!loc || !file) {
      const errors: { file?: string; location?: string; form?: string } = {};
      if (!loc) {
        errors.location = 'Select a location from the tree before uploading.';
      }
      if (!file) {
        errors.file = 'Choose a LAS or LAZ file before uploading.';
      }
      errors.form = 'Fix the highlighted fields to continue.';
      this.uploadFormErrors.set(errors);
      return;
    }

    this.clearUploadFormErrors();

    const fd = new FormData();
    fd.append('file', file);
    fd.append('scanType', this.scanUploadForm.scanType);
    fd.append('fileFormat', (file.name.split('.').pop() || 'LAZ').toUpperCase());

    this.saving.set(true);
    this.api.uploadLocationScanJob(loc.id, fd).subscribe({
      next: () => {
        this.saving.set(false);
        this.uploadScanOpen.set(false);
        this.showToast(`${this.scanUploadForm.scanType} scan uploaded`);
        if (this.locationId() === loc.id) {
          this.locationPanel?.reload();
        } else {
          this.selectLocation(loc.id);
        }
        this.loadAllScanJobs();
      },
      error: (err) => {
        this.saving.set(false);
        this.uploadFormErrors.set({
          form: err?.error?.message || 'Upload failed. Try again.',
        });
      },
    });
  }

  private clearUploadFormErrors(): void {
    this.uploadFormErrors.set({});
  }

  fileNameFromUri(uri: string | undefined | null): string {
    if (!uri) return 'No file uploaded';
    const parts = uri.split(/[/\\]/);
    return parts[parts.length - 1] || uri;
  }

  scanStatusClass(status: string): string {
    return `status-${(status || 'UPLOADED').toUpperCase()}`;
  }

  formatScanStatus(status: string | undefined): string {
    const key = (status || 'uploaded').toLowerCase();
    const labels: Record<string, string> = {
      uploaded: 'Uploaded',
      ready: 'Ready',
      processed: 'Processed',
      processing: 'Processing',
      failed: 'Failed',
    };
    return labels[key] ?? status ?? 'Unknown';
  }

  formatUploadDate(iso: string | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  }

  scanTypeLabel(scanType: string): string {
    const key = (scanType || '').toLowerCase();
    if (key === 'drone') return 'Drone';
    if (key === 'vehicle') return 'Vehicle';
    return scanType || 'Scan';
  }

  showToast(msg: string): void {
    this.toast.set(msg);
    setTimeout(() => this.toast.set(''), 3200);
  }
}
